import {usePrefersReducedMotion} from '@github-ui/brand-core/hooks/usePrefersReducedMotion'
import {getScrambleTextAnimation} from '@github-ui/brand-core/utils/animation'
import {XIcon} from '@primer/octicons-react'
import {Button, Heading, Label, Link, Text} from '@primer/react'
import {clsx} from 'clsx'
import {AnimatePresence} from 'motion/react'
import {useEffect, useRef, useState} from 'react'

import type {ProductAnnouncementModalView} from './../../ProductAnnouncement/ProductAnnouncement.types'
import ProductAnnouncementModalDropdown from './../ProductAnnouncementModalTabContent/ProductAnnouncementModalDropdown/ProductAnnouncementModalDropdown'
import ProductAnnouncementModalTabContent from './../ProductAnnouncementModalTabContent/ProductAnnouncementModalTabContent'
import ProductAnnouncementModalVisual from './../ProductAnnouncementModalVisual/ProductAnnouncementModalVisual'

import ModalStyles from './../ProductAnnouncementModal.module.css'
import Styles from './ProductAnnouncementModalDesktop.module.css'

export type ProductAnnouncementModalDesktopProps = ProductAnnouncementModalView

const defaultProps: Partial<ProductAnnouncementModalDesktopProps> = {}

const ProductAnnouncementModalDesktop = (props: ProductAnnouncementModalDesktopProps) => {
  const initializedProps: ProductAnnouncementModalDesktopProps = {...defaultProps, ...props}
  const {
    currentTabIndex,
    tabs,
    onTabChange,
    shouldShowTabCtas,
    shouldShowMainCta,
    shouldAnimateTitle,
    seeAll,
    cta1,
    cta2,
    dropdown,
    title,
    label,
    body,
    onClose,
    aria,
    onEvent,
    variant,
  } = initializedProps

  const [hoveredTabIndex, setHoveredTabIndex] = useState<number | null>(null)
  const shouldReduceMotion = usePrefersReducedMotion()
  const currentTabIndexRef = useRef<number>(currentTabIndex)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const titleTl = useRef<gsap.core.Timeline | null>(null)
  const currentTabName = tabs[currentTabIndex]?.title
  const currentTabEventPayload = {
    tabIndex: currentTabIndex,
    ...(currentTabName ? {tabName: currentTabName} : {}),
  }

  useEffect(() => {
    if (
      shouldAnimateTitle &&
      !shouldReduceMotion &&
      !window.matchMedia('(prefers-reduced-motion)').matches &&
      currentTabIndexRef.current === -1 &&
      headingRef.current
    ) {
      titleTl.current?.kill()
      titleTl.current = getScrambleTextAnimation(headingRef.current)
    }

    currentTabIndexRef.current = currentTabIndex
  }, [currentTabIndex, shouldAnimateTitle, shouldReduceMotion])

  useEffect(() => {
    return () => {
      titleTl.current?.kill()
    }
  }, [])

  return (
    <div
      className={clsx(
        Styles['ProductAnnouncementModalDesktop'],
        variant === 'security' && Styles['ProductAnnouncementModalDesktop--security'],
      )}
    >
      <button
        className={clsx(
          ModalStyles['ProductAnnouncementModal__closeButton'],
          Styles['ProductAnnouncementModalDesktop__closeButton'],
        )}
        onClick={onClose}
        aria-label={aria.close}
      >
        <XIcon size={24} />
      </button>

      <div className={Styles['ProductAnnouncementModalDesktop__sidebar']}>
        <div className={Styles['ProductAnnouncementModalDesktop__header']}>
          {label && (
            <Label className={Styles['ProductAnnouncementModalDesktop__label']} variant="success">
              {label}
            </Label>
          )}

          <Heading
            ref={headingRef}
            className={ModalStyles['ProductAnnouncementModal__title']}
            as="h1"
            variant="medium"
            aria-label={title}
          >
            <span aria-hidden="true">{title}</span>
          </Heading>

          {body && <p className={Styles['ProductAnnouncementModalDesktop__body']}>{body}</p>}
        </div>

        <div className={Styles['ProductAnnouncementModalDesktop__sidebar-content']}>
          <nav
            className={Styles['ProductAnnouncementModalDesktop__sidebar-items']}
            onMouseLeave={() => setHoveredTabIndex(null)}
          >
            <div
              className={Styles['ProductAnnouncementModalDesktop__sidebar-indicator']}
              style={{
                '--tabIndex': hoveredTabIndex ?? currentTabIndex,
              }}
            />
            {tabs.map((tab, index) => {
              return (
                <div
                  className={Styles['ProductAnnouncementModalDesktop__sidebar-item']}
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`item-${index}`}
                  aria-current={index === currentTabIndex}
                  onMouseEnter={() => setHoveredTabIndex(index)}
                >
                  <button onClick={() => onTabChange(index)} data-tab>
                    <Text weight="medium" size="medium">
                      {tab.title}
                    </Text>
                    {tab.isNew && (
                      <Label size="small" variant="secondary" className="ml-1">
                        New
                      </Label>
                    )}
                  </button>
                </div>
              )
            })}
          </nav>

          {seeAll?.href && (
            <div className={Styles['ProductAnnouncementModalDesktop__sidebar-seeAll']}>
              <Link
                href={seeAll.href}
                target={seeAll.isNewWindow ? '_blank' : undefined}
                onClick={() => onEvent?.('see_all_clicked')}
              >
                <Text size="large">{seeAll.label}</Text>
              </Link>
            </div>
          )}
        </div>

        {shouldShowMainCta && (
          <div
            className={clsx(
              Styles['ProductAnnouncementModalDesktop__sidebar-ctas'],
              dropdown && Styles['ProductAnnouncementModalDesktop__sidebar-ctas--stacked'],
            )}
          >
            {dropdown && <ProductAnnouncementModalDropdown data={dropdown} onEvent={onEvent} block />}

            {cta1 && (
              <Button
                as={cta1.href ? 'a' : 'button'}
                {...(cta1.href ? {href: cta1.href, target: cta1.isNewWindow ? '_blank' : undefined} : {})}
                block={!!dropdown}
                variant="primary"
                onClick={() => {
                  onEvent?.('main_cta1_clicked', {
                    ...currentTabEventPayload,
                    ctaId: 'main_cta1',
                    ...(tabs[currentTabIndex]?.mainCtaAnalyticsAction
                      ? {analyticsAction: tabs[currentTabIndex]?.mainCtaAnalyticsAction}
                      : {}),
                    ...(cta1.href ? {url: cta1.href} : {}),
                  })
                  if (!cta1.href) onClose()
                }}
              >
                {cta1.label}
              </Button>
            )}

            {cta2 && (
              <Button
                as={cta2.href ? 'a' : 'button'}
                {...(cta2.href ? {href: cta2.href, target: cta2.isNewWindow ? '_blank' : undefined} : {})}
                block={!!dropdown}
                onClick={() => {
                  onEvent?.('main_cta2_clicked', {
                    ...currentTabEventPayload,
                    ctaId: 'main_cta2',
                    ...(cta2.href ? {url: cta2.href} : {}),
                  })
                  if (!cta2.href) onClose()
                }}
              >
                {cta2.label}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className={Styles['ProductAnnouncementModalDesktop__content']}>
        <div className={Styles['ProductAnnouncementModalDesktop__content-visual']}>
          <ProductAnnouncementModalVisual
            currentTabIndex={currentTabIndex}
            visuals={tabs.map(tab => tab.visual)}
            aria={aria}
          />
        </div>

        <div className={Styles['ProductAnnouncementModalDesktop__content-items']}>
          <AnimatePresence mode="wait">
            {tabs.map(({title: tabTitle, heading, ...tab}, index) => {
              if (index !== currentTabIndex) return null
              return (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <div key={`item-${index}`}>
                  <ProductAnnouncementModalTabContent
                    {...tab}
                    title={heading}
                    shouldShowTabCtas={!!shouldShowTabCtas}
                    onEvent={(event, data = {}) => onEvent?.(event, {...data, tabName: tabTitle, tabIndex: index})}
                    onClose={onClose}
                  />
                </div>
              )
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

export default ProductAnnouncementModalDesktop
